import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { Mail, Settings, AlertCircle, Check, Info, ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';

interface EmailProvider {
  id: string;
  name: string;
  description: string;
  features: string[];
  pricing: {
    starter: {
      price: string;
      subscribers: string;
      features: string[];
    };
    pro: {
      price: string;
      subscribers: string;
      features: string[];
    };
    enterprise: {
      price: string;
      subscribers: string;
      features: string[];
    };
  };
}

const emailProviders: EmailProvider[] = [
  {
    id: 'mailchimp',
    name: 'Mailchimp',
    description: 'All-in-one marketing platform for growing businesses',
    features: [
      'Email Templates',
      'Automation Workflows',
      'List Segmentation',
      'A/B Testing',
      'Analytics & Reports'
    ],
    pricing: {
      starter: {
        price: '$11/mo',
        subscribers: 'Up to 500',
        features: [
          'Email Templates',
          'Basic Automation',
          'Basic Reports'
        ]
      },
      pro: {
        price: '$17/mo',
        subscribers: 'Up to 2,500',
        features: [
          'Advanced Automation',
          'A/B Testing',
          'Custom Templates',
          'Advanced Segmentation'
        ]
      },
      enterprise: {
        price: 'Custom',
        subscribers: 'Unlimited',
        features: [
          'Dedicated Support',
          'Advanced Security',
          'Custom Integrations',
          'Phone Support'
        ]
      }
    }
  },
  {
    id: 'constant_contact',
    name: 'Constant Contact',
    description: 'Easy-to-use email marketing and automation tools',
    features: [
      'Drag & Drop Editor',
      'Contact Management',
      'List Growth Tools',
      'Real-time Reporting',
      'Mobile App'
    ],
    pricing: {
      starter: {
        price: '$9.99/mo',
        subscribers: 'Up to 500',
        features: [
          'Email Templates',
          'Contact Management',
          'Basic Reports'
        ]
      },
      pro: {
        price: '$45/mo',
        subscribers: 'Up to 2,500',
        features: [
          'Automation Series',
          'Dynamic Content',
          'A/B Testing',
          'Advanced Analytics'
        ]
      },
      enterprise: {
        price: 'Custom',
        subscribers: 'Unlimited',
        features: [
          'Dedicated Support',
          'Custom Templates',
          'API Access',
          'Priority Support'
        ]
      }
    }
  },
  {
    id: 'sendgrid',
    name: 'SendGrid',
    description: 'Trusted email delivery and marketing automation',
    features: [
      'Email API',
      'Marketing Campaigns',
      'Transactional Email',
      'Email Validation',
      'Dedicated IPs'
    ],
    pricing: {
      starter: {
        price: '$15/mo',
        subscribers: 'Up to 5,000',
        features: [
          'Email API Access',
          'Basic Templates',
          'Basic Analytics'
        ]
      },
      pro: {
        price: '$60/mo',
        subscribers: 'Up to 10,000',
        features: [
          'Advanced Analytics',
          'Dedicated IP',
          'Sub-user Management',
          'Custom Templates'
        ]
      },
      enterprise: {
        price: 'Custom',
        subscribers: 'Unlimited',
        features: [
          'Enterprise Security',
          'Custom Integration',
          'Premium Support',
          'SLA Guarantees'
        ]
      }
    }
  }
];

const EmailProviderSettings: React.FC = () => {
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Fetch current provider settings
  const { data: currentSettings } = useQuery({
    queryKey: ['emailProviderSettings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_credentials')
        .select('provider, credentials')
        .eq('provider', 'email')
        .single();

      if (error) throw error;
      return data;
    }
  });

  // Save provider settings mutation
  const saveSettings = useMutation({
    mutationFn: async (settings: any) => {
      const { data, error } = await supabase
        .from('user_credentials')
        .upsert({
          provider: 'email',
          credentials: settings
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emailProviderSettings'] });
      toast.success('Email provider settings saved successfully');
    },
    onError: (error) => {
      toast.error('Failed to save settings');
      console.error('Settings save error:', error);
    }
  });

  const handleProviderSelect = (providerId: string) => {
    setSelectedProvider(providerId);
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center gap-2 mb-6">
          <Mail className="h-6 w-6 text-primary" />
          <h2 className="text-xl font-semibold">Email Service Providers</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {emailProviders.map((provider) => (
            <div
              key={provider.id}
              className={`border rounded-lg p-6 cursor-pointer transition-colors ${
                selectedProvider === provider.id
                  ? 'border-primary bg-primary/5'
                  : 'hover:border-gray-400'
              }`}
              onClick={() => handleProviderSelect(provider.id)}
            >
              <h3 className="text-lg font-semibold mb-2">{provider.name}</h3>
              <p className="text-gray-600 text-sm mb-4">{provider.description}</p>
              
              <div className="space-y-2 mb-4">
                {provider.features.map((feature, index) => (
                  <div key={index} className="flex items-center text-sm">
                    <Check className="h-4 w-4 text-primary mr-2" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>

              <button className="btn btn-primary w-full">
                {currentSettings?.provider === provider.id ? 'Configure' : 'Select Provider'}
                <ArrowRight className="h-4 w-4 ml-2" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {selectedProvider && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center gap-2 mb-6">
            <Settings className="h-6 w-6 text-primary" />
            <h2 className="text-xl font-semibold">Provider Settings</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {Object.entries(emailProviders.find(p => p.id === selectedProvider)?.pricing || {}).map(([tier, details]) => (
              <div key={tier} className="border rounded-lg p-6">
                <h3 className="text-lg font-semibold capitalize mb-2">{tier}</h3>
                <div className="text-2xl font-bold text-primary mb-2">{details.price}</div>
                <p className="text-gray-600 mb-4">{details.subscribers} subscribers</p>
                
                <div className="space-y-2">
                  {details.features.map((feature, index) => (
                    <div key={index} className="flex items-center text-sm">
                      <Check className="h-4 w-4 text-green-500 mr-2" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>

                <button className="btn btn-primary w-full mt-6">
                  Select Plan
                </button>
              </div>
            ))}
          </div>

          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <Info className="h-5 w-5 text-blue-500 mt-0.5 mr-2" />
              <div>
                <h4 className="text-sm font-medium text-blue-900">Integration Instructions</h4>
                <p className="mt-1 text-sm text-blue-700">
                  After selecting a plan, you'll need to configure your API keys and settings. 
                  Our integration wizard will guide you through the process step by step.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailProviderSettings;